import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { callFunction } from "../firebase";
import { UserContext } from "./AuthGuard";
import {
  getCurrentTimezone,
  getTimezoneMismatchSignature,
  readSeenTimezoneMismatch,
  rememberSeenTimezoneMismatch,
} from "../timezone";

const TimezoneMismatchPrompt = () => {
  const [user, setUser] = useContext(UserContext);
  const [detectedTimezone, setDetectedTimezone] = useState(() => getCurrentTimezone());
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const userId = user?.uid || user?.id;
  const savedTimezone = user?.timezone || "";
  const mismatchSignature = getTimezoneMismatchSignature(savedTimezone, detectedTimezone);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const refreshDetectedTimezone = () => {
      setDetectedTimezone(getCurrentTimezone());
    };

    window.addEventListener("focus", refreshDetectedTimezone);
    document.addEventListener("visibilitychange", refreshDetectedTimezone);

    return () => {
      window.removeEventListener("focus", refreshDetectedTimezone);
      document.removeEventListener("visibilitychange", refreshDetectedTimezone);
    };
  }, []);

  useEffect(() => {
    if (!userId || !mismatchSignature) {
      setOpen(false);
      setError("");
      return;
    }

    if (readSeenTimezoneMismatch(userId) === mismatchSignature) {
      setOpen(false);
      return;
    }

    setOpen(true);
  }, [mismatchSignature, userId]);

  const rememberChoice = () => {
    if (userId && mismatchSignature) {
      rememberSeenTimezoneMismatch(userId, mismatchSignature);
    }
  };

  const handleDismiss = () => {
    rememberChoice();
    setOpen(false);
    setError("");
  };

  const handleUseCurrentTimezone = async () => {
    if (!detectedTimezone) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      const response = await callFunction("users/profile", { timezone: detectedTimezone }, "PUT");

      if (response?.error || response?.ok === false) {
        setError(response?.error || "Failed to update timezone.");
        return;
      }

      rememberChoice();
      setUser((prev) => ({ ...(prev || {}), ...response, timezone: response?.timezone || detectedTimezone }));
      setOpen(false);
    } catch (_error) {
      setError("Failed to update timezone.");
    } finally {
      setSaving(false);
    }
  };

  if (!mismatchSignature) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleDismiss}
      aria-labelledby="timezone-mismatch-dialog-title"
    >
      <DialogTitle id="timezone-mismatch-dialog-title">
        Switch to your current timezone?
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Your current timezone is {detectedTimezone}, but Timer.Quest is set to {savedTimezone}.
          Switching now will keep planning days and reminders aligned with where you are.
        </DialogContentText>
        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss} disabled={saving}>
          Keep current setting
        </Button>
        <Button onClick={handleUseCurrentTimezone} variant="contained" disabled={saving}>
          Use {detectedTimezone}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimezoneMismatchPrompt;
